import { useCallback, useEffect, useState } from 'react';
import type { GameFileEntry } from '@volley/crucible-ipc-types/ipc-channels';
import { Button } from './ui/button';
import { useCrucibleClient } from '../transport/context.js';

interface FileNode {
  name: string;
  /** POSIX-style relative path from the game root, used as a stable React key. */
  path: string;
  type: 'file' | 'directory';
  children?: FileNode[];
}

/**
 * Reshape the flat list returned from the `game:files` IPC into a nested tree
 * the existing tree renderer can consume. The IPC sorts paths
 * lexicographically so a parent prefix always appears before its children.
 *
 * Exported so the renderer-side reshape can be unit-tested directly.
 */
export function buildTreeFromFlat(entries: GameFileEntry[]): FileNode[] {
  const root: FileNode[] = [];
  // Map of relative-path \u2192 that path's children array, for O(1) inserts.
  const childrenByPath = new Map<string, FileNode[]>();
  childrenByPath.set('', root);

  for (const entry of entries) {
    const parts = entry.path.split('/');
    const name = parts[parts.length - 1] ?? entry.path;
    const parentPath = parts.slice(0, -1).join('/');
    const parent = childrenByPath.get(parentPath);
    // Defensive: if the IPC returned a child before its parent the
    // parent lookup misses \u2014 skip rather than crash.
    if (!parent) continue;

    const node: FileNode = {
      name,
      path: entry.path,
      type: entry.isDirectory ? 'directory' : 'file',
    };
    if (entry.isDirectory) {
      node.children = [];
      childrenByPath.set(entry.path, node.children);
    }
    parent.push(node);
  }

  return root;
}

export function FilesPanel({
  gameId,
  collapsed,
  width,
  onToggleCollapse,
  onResize,
}: {
  gameId: string;
  collapsed: boolean;
  width: number;
  onToggleCollapse: () => void;
  onResize: (w: number) => void;
}): React.JSX.Element {
  const client = useCrucibleClient();
  const effectiveWidth = collapsed ? 40 : width;

  // Real file tree from `game:files` IPC (audit \u00A77 \u2014 the dead Files
  // panel previously rendered the same hard-coded `apps/display`,
  // `apps/controller`, `apps/server` tree for every game). The handler
  // returns a flat list of POSIX-style relative paths; we reshape into
  // a nested tree for the existing renderer. We refetch on gameId
  // change but skip the IPC entirely while the panel is collapsed.
  const [tree, setTree] = useState<FileNode[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (collapsed) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    client
      .invoke('game:files', { gameId })
      .then((entries) => {
        if (cancelled) return;
        setTree(buildTreeFromFlat(entries));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, gameId, collapsed]);

  return (
    <aside
      className="relative flex flex-col border-r border-white/[0.11] transition-[width] duration-200"
      style={{ width: effectiveWidth, minWidth: effectiveWidth, background: 'var(--bg-elev-1)' }}
    >
      {/* Toggle button */}
      <Button
        variant="ghost"
        onClick={onToggleCollapse}
        className="h-10 w-full rounded-none text-xs text-white/40 hover:text-white/60"
        title={collapsed ? 'Expand files' : 'Collapse files'}
        aria-label={collapsed ? 'Expand files' : 'Collapse files'}
      >
        {collapsed ? '\u25B6' : '\u25C0'}
      </Button>

      {/* File tree */}
      {!collapsed && (
        <div className="flex-1 overflow-auto px-2 pb-3">
          <h2 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-white/40">
            Files
          </h2>
          {loading && tree.length === 0 && (
            <p className="px-1 py-2 text-xs text-white/30">Loading files...</p>
          )}
          {loadError && <p className="px-1 py-2 text-xs text-error/70">{loadError}</p>}
          {!loading && !loadError && tree.length === 0 && (
            <p className="px-1 py-2 text-xs text-white/30">No files yet.</p>
          )}
          {tree.length > 0 && <FileTreeNode nodes={tree} depth={0} />}
        </div>
      )}

      {/* Resize handle */}
      {!collapsed && (
        <ResizeHandle
          side="right"
          currentSize={width}
          minSize={180}
          maxSize={400}
          onResize={onResize}
        />
      )}
    </aside>
  );
}

function FileTreeNode({ nodes, depth }: { nodes: FileNode[]; depth: number }): React.JSX.Element {
  return (
    <>
      {nodes.map((node) => (
        <FileTreeItem key={node.path} node={node} depth={depth} />
      ))}
    </>
  );
}

function FileTreeItem({ node, depth }: { node: FileNode; depth: number }): React.JSX.Element {
  const [expanded, setExpanded] = useState(depth < 2);
  const isDir = node.type === 'directory';
  const paddingLeft = 8 + depth * 14;

  return (
    <div>
      {/* File-tree row: use Button for app-region-no-drag + type="button" defaults;
          override rounded-full \u2192 rounded to match the tree-row visual (not a pill). */}
      <Button
        variant="ghost"
        onClick={() => isDir && setExpanded(!expanded)}
        className="w-full justify-start rounded-sm px-1 py-0.5 text-xs hover:bg-white/5"
        style={{ paddingLeft }}
        aria-expanded={isDir ? expanded : undefined}
      >
        {isDir ? (
          <span className="text-white/30">{expanded ? '\u25BE' : '\u25B8'}</span>
        ) : (
          <span className="w-3" />
        )}
        <span className={isDir ? 'text-white/60' : 'text-white/40'}>{node.name}</span>
      </Button>
      {isDir && expanded && node.children && (
        <FileTreeNode nodes={node.children} depth={depth + 1} />
      )}
    </div>
  );
}

function ResizeHandle({
  side,
  currentSize,
  minSize,
  maxSize,
  onResize,
}: {
  side: 'left' | 'right';
  currentSize: number;
  minSize: number;
  maxSize: number;
  onResize: (size: number) => void;
}): React.JSX.Element {
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startSize = currentSize;

      const onMouseMove = (moveEvent: MouseEvent): void => {
        const delta = moveEvent.clientX - startX;
        const newSize = side === 'right' ? startSize + delta : startSize - delta;
        onResize(Math.max(minSize, Math.min(maxSize, newSize)));
      };

      const onMouseUp = (): void => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [side, currentSize, minSize, maxSize, onResize],
  );

  return (
    <div
      onMouseDown={handleMouseDown}
      className={`absolute top-0 ${
        side === 'right' ? 'right-0 translate-x-1/2' : 'left-0 -translate-x-1/2'
      } z-10 h-full w-1.5 cursor-col-resize hover:bg-crucible-accent/30 active:bg-crucible-accent/50`}
    />
  );
}
